import React, { createContext, useState, useEffect, useCallback } from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';
import { COGNITO_CONFIG, INTERN_GROUP } from '../utils/constants';

const userPool = new CognitoUserPool({
  UserPoolId: COGNITO_CONFIG.UserPoolId,
  ClientId: COGNITO_CONFIG.ClientId,
});

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAuthenticated = !!user;
  // New applicants don't get the 'intern' Cognito group until admin approval,
  // but custom:intern_id is set at registration. Allow either signal so the
  // pre-approval onboarding flow (test, profile setup) isn't blocked.
  const isIntern = !!user && (user.groups?.includes(INTERN_GROUP) || !!user.custom_intern_id);

  const extractUserFromSession = useCallback((session) => {
    const idToken = session.getIdToken();
    const payload = idToken.decodePayload();
    const groups = payload['cognito:groups'] || [];
    const email = payload.email || payload.sub;

    localStorage.setItem('cwc_intern_id_token', idToken.getJwtToken());
    localStorage.setItem('cwc_intern_access_token', session.getAccessToken().getJwtToken());
    localStorage.setItem('cwc_intern_refresh_token', session.getRefreshToken().getToken());

    return {
      email,
      groups,
      sub: payload.sub,
      name: payload.name || payload.given_name || email,
      intern_id: payload['custom:intern_id'] || payload.sub,
      // Separate from intern_id (which falls back to sub) — this stays null
      // when the attribute is genuinely absent, so isIntern can use it as a
      // pre-approval onboarding signal.
      custom_intern_id: payload['custom:intern_id'] || null,
    };
  }, []);

  useEffect(() => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      setLoading(false);
      return;
    }

    cognitoUser.getSession((err, session) => {
      if (err || !session?.isValid()) {
        localStorage.removeItem('cwc_intern_id_token');
        localStorage.removeItem('cwc_intern_access_token');
        localStorage.removeItem('cwc_intern_refresh_token');
        setLoading(false);
        return;
      }

      const userData = extractUserFromSession(session);
      setUser(userData);
      setLoading(false);
    });
  }, [extractUserFromSession]);

  const login = useCallback((email, password, newPassword) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      const authDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session) => {
          const userData = extractUserFromSession(session);
          // Allow either the 'intern' group OR custom:intern_id (set at
          // registration before admin approval) so new applicants can sign in
          // for onboarding test + profile setup.
          if (!userData.groups.includes(INTERN_GROUP) && !userData.custom_intern_id) {
            cognitoUser.signOut();
            localStorage.removeItem('cwc_intern_id_token');
            localStorage.removeItem('cwc_intern_access_token');
            localStorage.removeItem('cwc_intern_refresh_token');
            reject(new Error('Access denied. Your account is not approved as an intern yet.'));
            return;
          }
          setUser(userData);
          resolve({ success: true });
        },
        onFailure: (err) => {
          reject(err);
        },
        newPasswordRequired: (userAttributes) => {
          if (newPassword) {
            // Cognito rejects non-mutable / non-writable attributes if they
            // are echoed back when completing the challenge. Strip them all
            // (phone_number_verified bit us when an admin got a phone number).
            delete userAttributes.email_verified;
            delete userAttributes.email;
            delete userAttributes.phone_number_verified;
            delete userAttributes.phone_number;
            cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
              onSuccess: (session) => {
                const userData = extractUserFromSession(session);
                setUser(userData);
                resolve({ success: true });
              },
              onFailure: (err) => {
                reject(err);
              },
            });
          } else {
            resolve({ challengeName: 'FORCE_CHANGE_PASSWORD' });
          }
        },
      });
    });
  }, [extractUserFromSession]);

  const changePassword = useCallback((oldPassword, newPassword) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) {
        reject(new Error('Not signed in'));
        return;
      }
      cognitoUser.getSession((sErr, session) => {
        if (sErr || !session?.isValid()) {
          reject(new Error('Session expired. Please sign in again.'));
          return;
        }
        cognitoUser.changePassword(oldPassword, newPassword, (err) => {
          if (err) reject(err);
          else resolve({ success: true });
        });
      });
    });
  }, []);

  const forgotPassword = useCallback((email) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });
      cognitoUser.forgotPassword({
        onSuccess: () => resolve({ success: true }),
        onFailure: (err) => reject(err),
        inputVerificationCode: () => resolve({ codeSent: true }),
      });
    });
  }, []);

  const confirmForgotPassword = useCallback((email, code, newPassword) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => resolve({ success: true }),
        onFailure: (err) => reject(err),
      });
    });
  }, []);

  const logout = useCallback(() => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    localStorage.removeItem('cwc_intern_id_token');
    localStorage.removeItem('cwc_intern_access_token');
    localStorage.removeItem('cwc_intern_refresh_token');
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isIntern, login, logout, changePassword, forgotPassword, confirmForgotPassword, loading }}>
      {children}
    </AuthContext.Provider>
  );
}
